import { config } from "@/config";
import { clearExpiresAt, isSessionExpiringSoon } from "@/features/auth/lib/token";
import { refreshNexusToken } from "@/features/auth/lib/refresh";
import { useServerStatusStore } from "@/hooks/serverStatusStore";
import { useAuth } from "@/features/auth/hooks/useAuth";
import i18n from "@/lib/i18n";
import { getElapsedSinceInteractionMs } from "@/lib/interaction-clock";
import { API_REQUEST_TIMEOUT_MS } from "@/lib/query-constants";

// ─── ApiError ───────────────────────────────────────────────────────

/**
 * Typed error for API responses. Preserves HTTP status and optional error code
 * so callers (including global QueryCache/MutationCache handlers) can branch on
 * `error.status` without parsing strings.
 */
export class ApiError extends Error {
  override readonly name = "ApiError";
  readonly orderId?: string;
  readonly orderStatus?: string;

  constructor(
    public readonly status: number,
    message: string,
    public readonly code?: string,
    extras?: { orderId?: string; orderStatus?: string },
  ) {
    super(message);
    this.orderId = extras?.orderId;
    this.orderStatus = extras?.orderStatus;
  }
}

export const GENERIC_API_ERROR_I18N_KEY = "common.error.generic";

const MAX_SAFE_MESSAGE_LENGTH = 200;

/**
 * Opt-in formatter: the caller is declaring the string is worth showing if it
 * also passes the leak gate. Customer toasts must not pass this.
 */
export function passThroughMessage(message: string): string {
  return message;
}

function isSafeUserFacingMessage(message: string): boolean {
  const trimmed = message.trim();
  if (trimmed.length < 1 || trimmed.length > MAX_SAFE_MESSAGE_LENGTH) return false;
  if (/[\r\n]/.test(trimmed)) return false;
  if (trimmed.startsWith("{") || trimmed.startsWith("[")) return false;
  if (/Invalid\s*`/.test(trimmed)) return false;
  if (/P2002|unique constraint/i.test(trimmed)) return false;
  if (/(?:^|[\s(=:'"])(?:\/(?:Users|app|usr|home|var|opt)\/|[A-Za-z]:\\)/.test(trimmed)) return false;
  if (/\/code\/nexus/.test(trimmed)) return false;
  if (/[a-z][a-z0-9+.-]*:\/\/[^\s]+:[^\s]+@/i.test(trimmed)) return false;
  if (/gaxios|stack trace|node:internal/i.test(trimmed)) return false;
  if (/Cannot read properties of|is not a function|is not defined|Unexpected token/i.test(trimmed)) return false;
  if (/\bat\s+\S+:\d+/.test(trimmed)) return false;
  return true;
}

function isAuthStatus(status: number): boolean {
  return status === 401 || status === 403;
}

/** R3: local mutation toasts must not fire for auth errors — global handler owns session UX. */
export function shouldSuppressLocalApiErrorToast(error: unknown): boolean {
  return error instanceof ApiError && isAuthStatus(error.status);
}

function isSurfacableClientError(status: number): boolean {
  return status >= 400 && status < 500 && !isAuthStatus(status);
}

/**
 * Default: generic i18n copy. Pass `format` only when the call site trusts the
 * backend/worker text (operator screens, sanitised Amber FAILED). 401/403 and
 * 5xx never surface. Every formatted string still goes through the leak gate.
 */
export function getUserFacingApiMessage(
  error?: unknown,
  format?: (message: string) => string,
): string {
  const generic = i18n.t(GENERIC_API_ERROR_I18N_KEY);

  if (error instanceof ApiError && !isSurfacableClientError(error.status)) {
    return generic;
  }

  if (!format || !(error instanceof ApiError)) return generic;

  const raw = error.message;
  if (!raw) return generic;

  const formatted = format(raw);
  return isSafeUserFacingMessage(formatted) ? formatted : generic;
}

/**
 * Like {@link getUserFacingApiMessage} but returns `null` for 401/403 so local
 * mutation handlers can skip the toast entirely (R3).
 */
export function getLocalUserFacingApiMessage(
  error?: unknown,
  format?: (message: string) => string,
): string | null {
  if (shouldSuppressLocalApiErrorToast(error)) return null;
  return getUserFacingApiMessage(error, format);
}

// ─── Types ──────────────────────────────────────────────────────────

interface ApiEnvelope {
  success: boolean;
  data?: unknown;
  correlationId?: string;
  message?: string;
}

interface AuthFetchOptions extends RequestInit {
  /** When `true`, a 404 response returns `null` instead of throwing. */
  nullOn404?: boolean;
  /** When `true`, returns the raw `Response` (skips JSON parsing). Use for blob/stream endpoints. */
  rawResponse?: boolean;
  /**
   * Per-request abort timeout (ms). Defaults to `API_REQUEST_TIMEOUT_MS`.
   * Use `API_REQUEST_TIMEOUT_LONG_MS` for legitimately-heavy endpoints
   * (large tenant-wide lists, permission resolution, etc.) so a cold pod
   * + real DB roundtrip isn't aborted mid-flight.
   */
  timeoutMs?: number;
  /** When `true`, never inject the active sidebar tenant as `X-Tenant-Id`. */
  shouldOmitTenantHeader?: boolean;
}

interface AuthFetchResult<T> {
  data: T;
  correlationId?: string;
}

// ─── Helpers ────────────────────────────────────────────────────────

const MUTATING_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

function buildHeaders(fetchOptions: AuthFetchOptions, shouldOmitTenantHeader = false): HeadersInit {
  const body = fetchOptions.body;
  const useJsonContentType =
    body != null &&
    !(typeof FormData !== "undefined" && body instanceof FormData) &&
    !(typeof URLSearchParams !== "undefined" && body instanceof URLSearchParams);

  const headers: Record<string, string> = {
    ...(useJsonContentType ? { "Content-Type": "application/json" } : {}),
    ...(fetchOptions.headers as Record<string, string> | undefined),
  };

  const { activeTenantId, csrfToken } = useAuth.getState();

  // Callers (IAM org settings) may already set X-Tenant-Id for a non-active tenant.
  // Do not overwrite an explicit header with the sidebar's active tenant.
  if (!shouldOmitTenantHeader && activeTenantId && !headers["X-Tenant-Id"]) {
    headers["X-Tenant-Id"] = activeTenantId;
  }

  const method = (fetchOptions.method ?? "GET").toUpperCase();
  if (MUTATING_METHODS.has(method)) {
    if (csrfToken) {
      headers["X-CSRF-Token"] = csrfToken;
    }

    // Mutations are the user-initiated calls, and the only ones that produce an
    // EventJob row. Reporting the click gap lets the server measure the wait
    // from the click rather than from the publish.
    const elapsedMs = getElapsedSinceInteractionMs();
    if (elapsedMs !== null) {
      headers["X-Client-Elapsed-Ms"] = String(elapsedMs);
    }
  }

  return headers;
}

// ─── authFetch ──────────────────────────────────────────────────────

/**
 * Unified authenticated fetch for all API calls. Handles:
 * - Cookie-based auth via `credentials: 'include'`
 * - CSRF token injection on mutating requests
 * - Proactive refresh when session is expiring soon
 * - JSON envelope unwrapping `{ success, data, correlationId }`
 * - Typed error responses via `ApiError`
 */
export async function authFetch<T = unknown>(
  url: string,
  options: AuthFetchOptions & { rawResponse: true },
): Promise<Response>;
export async function authFetch<T = unknown>(
  url: string,
  options: AuthFetchOptions & { nullOn404: true },
): Promise<AuthFetchResult<T | null>>;
export async function authFetch<T = unknown>(
  url: string,
  options?: AuthFetchOptions,
): Promise<AuthFetchResult<T>>;
export async function authFetch<T = unknown>(
  url: string,
  options: AuthFetchOptions = {},
): Promise<Response | AuthFetchResult<T | null>> {
  const { nullOn404, rawResponse, timeoutMs, shouldOmitTenantHeader, ...fetchOptions } = options;
  const requestTimeoutMs = timeoutMs ?? API_REQUEST_TIMEOUT_MS;

  if (!useAuth.getState().isAuthenticated) {
    throw new ApiError(401, "Session expired", "TOKEN_EXPIRED");
  }

  if (useServerStatusStore.getState().status === "offline") {
    throw new ApiError(0, "You are offline", "OFFLINE");
  }

  // ── Proactive refresh if session is expiring soon ─────────────────
  if (isSessionExpiringSoon()) {
    try {
      await refreshNexusToken();
    } catch {
      // Proactive refresh failed — cookies may still be valid for a few
      // more seconds. Continue; if 401, the reactive path handles it.
    }
  }

  // ── Send the request ───────────────────────────────────────────
  let res: Response;
  try {
    res = await fetch(`${config.apiUrl}${url}`, {
      ...fetchOptions,
      credentials: "include",
      headers: buildHeaders(fetchOptions, shouldOmitTenantHeader),
      signal: AbortSignal.timeout(requestTimeoutMs),
    });
  } catch (err) {
    const isTimeout = err instanceof DOMException && err.name === "TimeoutError";
    throw new ApiError(
      0,
      err instanceof Error ? err.message : "Network error",
      isTimeout ? "TIMEOUT" : "NETWORK_ERROR",
    );
  }

  // ── Reactive refresh on 401 ────────────────────────────────────
  if (res.status === 401) {
    if (!useAuth.getState().isAuthenticated) {
      clearExpiresAt();
      throw new ApiError(401, "Session expired", "TOKEN_EXPIRED");
    }
    try {
      await refreshNexusToken();
      res = await fetch(`${config.apiUrl}${url}`, {
        ...fetchOptions,
        credentials: "include",
        headers: buildHeaders(fetchOptions, shouldOmitTenantHeader),
        signal: AbortSignal.timeout(requestTimeoutMs),
      });
    } catch {
      clearExpiresAt();
      throw new ApiError(401, "Session expired", "TOKEN_EXPIRED");
    }
  }

  // ── Reactive recovery on CSRF failure ───────────────────────────
  // Happens when the `_csrf` cookie was cleared (devtools, COOKIE_SECRET
  // rotated across worktrees) but localStorage still says we're logged in.
  // /auth/refresh issues a fresh `_csrf` cookie + token; retry once.
  if (res.status === 403 && MUTATING_METHODS.has((fetchOptions.method ?? "GET").toUpperCase())) {
    const peek = res.clone();
    const body = (await peek.json().catch(() => null)) as { error?: { code?: string } } | null;
    if (body?.error?.code === "CSRF_INVALID") {
      try {
        await refreshNexusToken();
        res = await fetch(`${config.apiUrl}${url}`, {
          ...fetchOptions,
          credentials: "include",
          headers: buildHeaders(fetchOptions, shouldOmitTenantHeader),
          signal: AbortSignal.timeout(requestTimeoutMs),
        });
      } catch {
        // refresh failed — fall through to the normal !res.ok path below
      }
    }
  }

  if (!res.ok) {
    if (nullOn404 && res.status === 404) {
      return { data: null as T | null };
    }

    const body = await res.json().catch(() => ({}) as Record<string, unknown>);
    // Backend wraps errors as { error: { code, message, statusCode } }
    const envelope = (body as Record<string, unknown>).error as Record<string, unknown> | undefined;
    const orderId =
      typeof (body as Record<string, unknown>).orderId === "string"
        ? ((body as Record<string, unknown>).orderId as string)
        : undefined;
    const orderStatus =
      typeof (body as Record<string, unknown>).status === "string"
        ? ((body as Record<string, unknown>).status as string)
        : undefined;
    throw new ApiError(
      res.status,
      (envelope?.message ?? (body as Record<string, unknown>).message ?? `Request failed: ${res.status}`) as string,
      (envelope?.code ?? (body as Record<string, unknown>).code) as string | undefined,
      orderId || orderStatus ? { orderId, orderStatus } : undefined,
    );
  }

  if (rawResponse) {
    return res;
  }

  const json = (await res.json()) as ApiEnvelope;
  return {
    data: json.data as T,
    correlationId: json.correlationId,
  };
}
